import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid as MuiGrid,
  TablePagination,
  InputAdornment
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import agentService, { Agent, AgentQueryParams } from '../../services/agentService';

// Custom Grid component to handle MUI v7 properly
const Grid = MuiGrid;

const AgentList: React.FC = () => {
  const [allAgents, setAllAgents] = useState<Agent[]>([]);
  const [totalAgentCount, setTotalAgentCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState<boolean>(false);
  const isInitialMount = useRef(true);
  
  // Pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  
  // Search and filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filters, setFilters] = useState<{
    country: string;
    state: string;
    city: string;
  }>({
    country: '',
    state: '',
    city: ''
  });
  
  // Filter options
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  
  const navigate = useNavigate();

  // Reference to current state values to access in callbacks
  const stateRef = useRef({
    page,
    rowsPerPage,
    searchTerm,
    filters
  });
  
  // Update the ref whenever these values change
  useEffect(() => {
    stateRef.current = {
      page,
      rowsPerPage,
      searchTerm,
      filters
    };
  }, [page, rowsPerPage, searchTerm, filters]);

  // Fetch all agents and handle search/filter
  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Access the latest state values from the ref
      const { page, rowsPerPage, searchTerm, filters } = stateRef.current;
      
      // Prepare query parameters for the API
      const queryParams: AgentQueryParams = {
        page: page + 1, // Add 1 because backend uses 1-based indexing
        page_size: rowsPerPage
      };
      
      // Add search parameter if present
      if (searchTerm) {
        queryParams.search = searchTerm;
      }
      
      // Add filter parameters if present
      if (filters.country) {
        queryParams.country = filters.country;
      }
      
      if (filters.state) {
        queryParams.state = filters.state;
      }
      
      if (filters.city) {
        queryParams.city = filters.city;
      }
      
      const response = await agentService.getAgents(queryParams);
      setAllAgents(response.results);
      setTotalAgentCount(response.count);
      
      // Extract unique filter values if not already populated
      if (countries.length === 0) {
        extractFilterOptions(response.results);
      }
    } catch (err) {
      console.error('Failed to fetch agents:', err);
      setError('Failed to load agents. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed, using ref instead
  
  // Use server-side pagination
  const paginatedAgents = allAgents;
  
  const extractFilterOptions = (agents: Agent[]) => {
    const uniqueCountries = Array.from(new Set(agents.map(agent => agent.country).filter(Boolean) as string[]));
    const uniqueStates = Array.from(new Set(agents.map(agent => agent.state).filter(Boolean) as string[]));
    const uniqueCities = Array.from(new Set(agents.map(agent => agent.city).filter(Boolean) as string[]));
    
    setCountries(uniqueCountries);
    setStates(uniqueStates);
    setCities(uniqueCities);
  };

  // Load agents on initial mount
  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);
  
  // Debounced effect for changes that should trigger a refresh
  useEffect(() => {
    // Don't run on the initial render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    const timer = setTimeout(() => {
      fetchAgents();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [page, rowsPerPage, searchTerm, filters.country, filters.state, filters.city, fetchAgents]);
  
  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Direct search handling (no separate debounced handler needed)
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page on search
  };
  
  const handleCreateNew = () => {
    navigate('/agents/new');
  };

  const handleEdit = (id: number) => {
    navigate(`/agents/edit/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/agents/${id}/reservations`);
  };

  const handleDeleteClick = (agent: Agent) => {
    setAgentToDelete(agent);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!agentToDelete || !agentToDelete.id) return;
    
    try {
      await agentService.deleteAgent(agentToDelete.id);
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
      fetchAgents(); // Refresh the list
    } catch (err) {
      console.error('Failed to delete agent:', err);
      setError('Failed to delete agent. Please try again later.');
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAgentToDelete(null);
  };
  
  const clearSearch = () => {
    setSearchTerm('');
    setPage(0); // Reset to first page
  };
  
  const handleFilterChange = (name: keyof typeof filters) => (event: React.ChangeEvent<{ value: unknown }> | any) => {
    setFilters({
      ...filters,
      [name]: event.target.value as string
    });
  };
  
  const applyFilters = () => {
    setPage(0); // Reset to first page when applying filters
    setFilterDialogOpen(false);
    // fetchAgents is called automatically via the useEffect
  };
  
  const clearFilters = () => {
    setFilters({
      country: '',
      state: '',
      city: ''
    });
    setFilterDialogOpen(false);
    setPage(0);
    // fetchAgents is called automatically via the useEffect
  };

  // Content rendering
  if (loading && allAgents.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Agents
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
        >
          Create New Agent
        </Button>
      </Box>

      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      
      {/* Search and filter controls */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Search agents..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
        <Tooltip title="Filter">
          <Button 
            variant="outlined" 
            startIcon={<FilterIcon />}
            onClick={() => setFilterDialogOpen(true)}
            color={Object.values(filters).some(Boolean) ? "primary" : "inherit"}
          >
            Filter
          </Button>
        </Tooltip>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>State/City</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAgents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No agents found. Create a new agent to get started.
                </TableCell>
              </TableRow>
            ) : (
              paginatedAgents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell>{agent.name}</TableCell>
                  <TableCell>{agent.code || '-'}</TableCell>
                  <TableCell>{agent.country || '-'}</TableCell>
                  <TableCell>
                    {agent.state ? `${agent.state}, ` : ''}
                    {agent.city || '-'}
                  </TableCell>
                  <TableCell>
                    {agent.email ? (
                      <Box>
                        Email: {agent.email}<br />
                        Phone: {agent.phone || '-'}
                      </Box>
                    ) : (
                      <Box>
                        Phone: {agent.phone || '-'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton 
                        color="info" 
                        onClick={() => handleView(agent.id)}
                        size="small"
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton 
                        color="primary" 
                        onClick={() => handleEdit(agent.id)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteClick(agent)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalAgentCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the agent "{agentToDelete?.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Filter Dialog */}
      <Dialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Filter Agents</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Country</InputLabel>
              <Select
                value={filters.country}
                onChange={handleFilterChange('country')}
                label="Country"
              >
                <MenuItem value="">All Countries</MenuItem>
                {countries.map(country => (
                  <MenuItem key={country} value={country}>{country}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select
                value={filters.state}
                onChange={handleFilterChange('state')}
                label="State"
              >
                <MenuItem value="">All States</MenuItem>
                {states.map(state => (
                  <MenuItem key={state} value={state}>{state}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small">
              <InputLabel>City</InputLabel>
              <Select
                value={filters.city}
                onChange={handleFilterChange('city')}
                label="City"
              >
                <MenuItem value="">All Cities</MenuItem>
                {cities.map(city => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear Filters</Button>
          <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
          <Button onClick={applyFilters} variant="contained" color="primary">
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentList; 